"use client";

/**
 * BlogInteractiveLayer — Client-side interactivity for blog article pages.
 *
 * SSR/server concerns (markdown rendering, schema, SEO) are in page.tsx + MarkdownRenderer.
 * This component ONLY handles interactive behaviors — no layout-affecting elements:
 *   1. Reading progress bar (fixed top — no layout impact)
 *   2. Active TOC section highlighting via DOM manipulation on #blog-toc-static
 *      (does NOT render its own TOC — uses SSR aside TOC structure)
 *   3. Scroll-to-top button (fixed — no layout impact)
 *   4. Code block copy-to-clipboard buttons (injected into pre elements)
 *   5. Mobile TOC bottom sticky bar (fixed bottom — no layout impact)
 *
 * CRITICAL: This component renders only fixed/absolute elements + null.
 * It MUST NOT render any block/inline elements into the document flow —
 * doing so breaks the article grid layout.
 *
 * Pattern: progressive enhancement — if JS fails, page is still 100% readable.
 */

import { useEffect, useState, useCallback, useRef } from "react";

interface TocItem {
  id: string;
  label: string;
  level: 2 | 3;
}

interface BlogInteractiveLayerProps {
  tocItems: TocItem[];
}

export default function BlogInteractiveLayer({
  tocItems,
}: BlogInteractiveLayerProps) {
  const [activeId, setActiveId] = useState<string>("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMobileToc, setShowMobileToc] = useState(false);
  const activeIdRef = useRef("");
  const showScrollTopRef = useRef(false);
  const showMobileTocRef = useRef(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const mobileProgressRef = useRef<HTMLDivElement>(null);
  const mobileProgressTextRef = useRef<HTMLSpanElement>(null);

  // ── 1. Reading progress bar + scroll-top + mobile TOC visibility ──────────
  useEffect(() => {
    let rafId: number | null = null;

    const updateScrollState = () => {
      rafId = null;
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      const progress = total > 0 ? Math.min(100, (scrollTop / total) * 100) : 0;
      const width = `${progress}%`;

      if (progressBarRef.current) progressBarRef.current.style.width = width;
      if (mobileProgressRef.current)
        mobileProgressRef.current.style.width = width;
      if (mobileProgressTextRef.current) {
        mobileProgressTextRef.current.textContent = `${Math.round(progress)}%`;
      }

      const nextShowScrollTop = scrollTop > 400;
      if (nextShowScrollTop !== showScrollTopRef.current) {
        showScrollTopRef.current = nextShowScrollTop;
        setShowScrollTop(nextShowScrollTop);
      }

      const nextShowMobileToc = scrollTop > 300 && tocItems.length > 0;
      if (nextShowMobileToc !== showMobileTocRef.current) {
        showMobileTocRef.current = nextShowMobileToc;
        setShowMobileToc(nextShowMobileToc);
      }
    };

    const onScroll = () => {
      if (rafId == null) {
        rafId = window.requestAnimationFrame(updateScrollState);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    updateScrollState();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [tocItems.length]);

  // ── 2. Active TOC via DOM manipulation on SSR static TOC ─────────────────
  // BlogInteractiveLayer does NOT render its own TOC.
  // It updates the SSR aside TOC (#blog-toc-static) link active states via
  // data-active attribute → CSS handles the visual highlight.
  useEffect(() => {
    if (!tocItems.length) return;

    const headingEls = tocItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (!headingEls.length) return;

    const visibleIds = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id);
          } else {
            visibleIds.delete(entry.target.id);
          }
        }

        const active = tocItems.find((item) => visibleIds.has(item.id));
        const nextActiveId = active?.id || "";

        if (nextActiveId !== activeIdRef.current) {
          activeIdRef.current = nextActiveId;
          setActiveId(nextActiveId);

          // DOM: update data-active on SSR static TOC links
          const tocContainer = document.getElementById("blog-toc-static");
          if (tocContainer) {
            tocContainer
              .querySelectorAll("a[data-active]")
              .forEach((el) => el.removeAttribute("data-active"));
            if (nextActiveId) {
              const activeLink = tocContainer.querySelector(
                `a[href="#${nextActiveId}"]`
              );
              activeLink?.setAttribute("data-active", "");
            }
          }
        }
      },
      {
        rootMargin: "-80px 0px -70% 0px",
        threshold: 0,
      }
    );

    headingEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [tocItems]);

  // ── 3. Copy-to-clipboard for code blocks ─────────────────────────────────
  useEffect(() => {
    const codeBlocks = document.querySelectorAll("pre > code");
    const cleanup: (() => void)[] = [];

    codeBlocks.forEach((code) => {
      const pre = code.parentElement;
      if (!pre || pre.dataset.hasCopyBtn) return;
      pre.style.position = "relative";
      pre.dataset.hasCopyBtn = "1";

      const btn = document.createElement("button");
      btn.textContent = "Kopyala";
      btn.setAttribute("aria-label", "Kodu kopyala");
      btn.style.cssText = [
        "position:absolute",
        "top:8px",
        "right:8px",
        "padding:2px 10px",
        "font-size:11px",
        "border-radius:6px",
        "background:rgba(255,255,255,0.15)",
        "color:#e2e8f0",
        "border:1px solid rgba(255,255,255,0.2)",
        "cursor:pointer",
        "transition:background 0.15s",
      ].join(";");

      const onClick = async () => {
        try {
          await navigator.clipboard.writeText(code.textContent || "");
          btn.textContent = "Kopyalandı ✓";
          setTimeout(() => (btn.textContent = "Kopyala"), 2000);
        } catch {
          btn.textContent = "Hata";
        }
      };

      btn.addEventListener("click", onClick);
      pre.appendChild(btn);
      cleanup.push(() => btn.removeEventListener("click", onClick));
    });

    return () => cleanup.forEach((fn) => fn());
  }, []);

  // ── 4. Scroll-to-top ─────────────────────────────────────────────────────
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Active label for mobile TOC
  const activeLabel = tocItems.find((t) => t.id === activeId)?.label || null;

  // ── Render — ONLY fixed/pointer-events-none elements ─────────────────────
  // Nothing here affects document flow or the article grid layout.
  return (
    <>
      {/* Reading progress bar — fixed, no layout impact */}
      <div
        aria-hidden="true"
        ref={progressBarRef}
        className="pointer-events-none fixed left-0 top-0 z-50 h-[3px] bg-teal-500 transition-all duration-100"
        style={{ width: "0%" }}
      />

      {/* UX-006: Mobile TOC bottom sticky bar — fixed bottom, no layout impact */}
      {showMobileToc && tocItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
          <div className="bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg">
            {/* Active section indicator row */}
            <div className="flex items-center gap-2 px-4 py-2.5">
              {/* Progress pill */}
              <div className="flex-shrink-0 flex items-center gap-1.5">
                <div className="h-1 w-8 overflow-hidden rounded-full bg-slate-200">
                  <div
                    ref={mobileProgressRef}
                    className="h-full rounded-full bg-teal-500 transition-all duration-200"
                    style={{ width: "0%" }}
                  />
                </div>
                <span
                  ref={mobileProgressTextRef}
                  className="text-xs text-slate-400 tabular-nums"
                >
                  0%
                </span>
              </div>

              {/* Active section label */}
              <div className="flex-1 min-w-0">
                {activeLabel ? (
                  <p className="truncate text-xs font-medium text-slate-700">
                    <span className="mr-1 text-teal-600">§</span>
                    {activeLabel}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">İçindekiler</p>
                )}
              </div>

              {/* Scroll to top */}
              {showScrollTop && (
                <button
                  onClick={scrollToTop}
                  aria-label="Sayfanın başına dön"
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-sm text-slate-600 transition-colors hover:bg-teal-50 hover:text-teal-700"
                >
                  ↑
                </button>
              )}
            </div>

            {/* H2 chips — horizontally scrollable */}
            <div className="overflow-x-auto border-t border-slate-100">
              <div className="flex gap-1 px-4 py-2 whitespace-nowrap">
                {tocItems
                  .filter((t) => t.level === 2)
                  .map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={[
                        "inline-flex items-center px-2.5 py-1 rounded-md text-xs transition-all",
                        activeId === item.id
                          ? "bg-teal-50 text-teal-700 font-semibold"
                          : "bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700",
                      ].join(" ")}
                    >
                      {item.label}
                    </a>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop scroll-to-top — fixed, no layout impact */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          aria-label="Sayfanın başına dön"
          className={[
            "fixed bottom-6 right-6 z-40 hidden lg:flex",
            "w-10 h-10 rounded-md bg-slate-950 text-white shadow-lg",
            "items-center justify-center",
            "hover:bg-teal-700 transition-colors",
            "text-lg leading-none",
          ].join(" ")}
        >
          ↑
        </button>
      )}
    </>
  );
}
